import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { LayoutFactory, PageLayoutType } from '../../layout/LayoutFactory';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { authTokenState, roleAccessState } from '../../atoms/auth/auth.atom';
import { useMutation } from '@tanstack/react-query';
import axios, { SuccessResponse } from '../../libraries/axios/axios';
import { MappedRoleAccess } from '../../domain/Role';
import { DefaultError } from '@tanstack/query-core/build/modern/index';
import { apiRoutes } from '../../../config/api/apiRoutes';
import {
  ProgressFactory,
  ProgressVariant,
} from '../../common/Progress/ProgressFactory';
import { ROUTER_CONFIG } from '../../../config/router/router';
import { companyState } from '../../atoms/company/company.atom';
import { currentUserState } from '../../atoms/user/user.atom';

export const RouteProvider = () => {
  const authStateValue = useRecoilValue(authTokenState);
  const companyStateValue = useRecoilValue(companyState);
  const currentUser = useRecoilValue(currentUserState);
  const setRoleAccessStateValue = useSetRecoilState(roleAccessState);

  const { mutate: getMappedRoleAccess, isPending } = useMutation<
    SuccessResponse<MappedRoleAccess[]>,
    DefaultError
  >({
    mutationFn: () => axios.get(apiRoutes.roles.mappedAccess),
    onSuccess: (response) => {
      setRoleAccessStateValue(response.data);
    },
  });

  useEffect(() => {
    if (authStateValue && currentUser) {
      getMappedRoleAccess();
    }
  }, [authStateValue, currentUser?.identity, companyStateValue]);

  const adminPages = useMemo(() => {
    if (authStateValue) {
      return ROUTER_CONFIG.admin.map((route, index) => (
        <Route
          key={`admin-${index}`}
          path={route.path}
          element={
            route.navigate ? (
              <Navigate to={route.navigate} replace />
            ) : (
              route.element
            )
          }
        />
      ));
    }

    return ROUTER_CONFIG.adminNonAuth.map((route, index) => (
      <Route
        key={`admin-non-auth-${index}`}
        path={route.path}
        element={
          route.navigate ? (
            <Navigate to={route.navigate} replace />
          ) : (
            route.element
          )
        }
      />
    ));
  }, [authStateValue]);

  const authPages = useMemo(() => {
    if (authStateValue) {
      return ROUTER_CONFIG.auth.map((route, index) => (
        <Route
          key={`auth-${index}`}
          path={route.path}
          element={
            route.navigate ? (
              <Navigate to={route.navigate} replace />
            ) : (
              route.element
            )
          }
        />
      ));
    }

    return ROUTER_CONFIG.authNonAuth.map((route, index) => (
      <Route
        key={`auth-non-auth-${index}`}
        path={route.path}
        element={
          route.navigate ? (
            <Navigate to={route.navigate} replace />
          ) : (
            route.element
          )
        }
      />
    ));
  }, [authStateValue]);

  if (authStateValue && isPending) {
    return <ProgressFactory variant={ProgressVariant.linear} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<LayoutFactory variant={PageLayoutType.admin} />}>
          {adminPages}
        </Route>
        <Route element={<LayoutFactory variant={PageLayoutType.auth} />}>
          {authPages}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default RouteProvider;
